<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-100" @close="handleDialogClose">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-100 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <form @submit.prevent="submit" class="flex flex-col divide-y divide-gray-200 bg-white">
                                <div class="px-4 py-4 sm:px-6">
                                    <div class="flex items-center justify-between">
                                        <DialogTitle class="text-base font-semibold text-gray-900">
                                            {{ $t('edit_folder_modal.title') }}
                                        </DialogTitle>
                                        <button
                                            type="button"
                                            class="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:ring-2 focus-visible:ring-royal-blue-500 focus:outline-hidden"
                                            @click="close"
                                        >
                                            <span class="absolute -inset-2.5" />
                                            <span class="sr-only">{{ $t('edit_folder_modal.cancel') }}</span>
                                            <XMarkIcon class="size-6" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>

                                <div class="divide-y divide-gray-200 px-4 sm:px-6">
                                    <div class="space-y-6 pt-5 pb-5">
                                        <div>
                                            <label for="title" class="block text-sm/6 font-medium text-gray-900">
                                                {{ $t('create_gallery.name') }}
                                            </label>
                                            <div class="mt-2 grid grid-cols-1">
                                                <input
                                                    id="title"
                                                    v-model="form.title"
                                                    type="text"
                                                    autocomplete="title"
                                                    :class="{
                                                        'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6': true,
                                                        'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('title')
                                                    }"
                                                />
                                                <ExclamationCircleIcon
                                                    v-if="hasError('title')"
                                                    class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <p class="mt-2 text-sm text-red-600" v-if="hasError('title')">
                                                {{ getError('title') }}
                                            </p>
                                        </div>

                                        <div>
                                            <SwitchGroup as="div" class="flex items-center justify-between">
                                                <span class="flex grow flex-col mr-3">
                                                    <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>
                                                        {{ $t('edit_folder_modal.hide_label') }}
                                                    </SwitchLabel>
                                                    <SwitchDescription as="span" class="text-sm text-gray-500">
                                                        {{ $t('edit_folder_modal.hide_description') }}
                                                    </SwitchDescription>
                                                </span>
                                                <Switch v-model="form.is_hidden" :class="[form.is_hidden ? 'bg-royal-blue-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden']">
                                                    <span aria-hidden="true" :class="[form.is_hidden ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out']" />
                                                </Switch>
                                            </SwitchGroup>
                                        </div>

                                        <div>
                                            <SwitchGroup as="div" class="flex items-center justify-between">
                                                <span class="flex grow flex-col mr-3">
                                                    <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>
                                                        {{ $t('edit_folder_modal.hide_photos_label') }}
                                                    </SwitchLabel>
                                                    <SwitchDescription as="span" class="text-sm text-gray-500">
                                                        {{ $t('edit_folder_modal.hide_photos_description') }}
                                                    </SwitchDescription>
                                                </span>
                                                <Switch v-model="form.hide_photos" :class="[form.hide_photos ? 'bg-royal-blue-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden']">
                                                    <span aria-hidden="true" :class="[form.hide_photos ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out']" />
                                                </Switch>
                                            </SwitchGroup>
                                        </div>
                                    </div>

                                    <div class="space-y-6 pt-5 pb-5">
                                        <div>
                                            <label for="folder-url" class="block text-sm/6 font-medium text-gray-900">
                                                {{ $t('edit_folder_modal.url_label') }}
                                            </label>
                                            <div class="mt-3 grid grid-cols-1">
                                                <input type="text" readonly id="folder-url" class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 sm:pr-9 sm:text-sm/6" :value="form.public_url" />
                                                <svg @click.prevent="copyUrl" class="cursor-pointer col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-gray-400 sm:size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18"><g transform="translate(1 1)" stroke="#000" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><rect width="10.4" height="10.4" x="5.6" y="5.6" rx="1.6"></rect><path d="M2.4 10.4h-.8A1.6 1.6 0 010 8.8V1.6A1.6 1.6 0 011.6 0h7.2a1.6 1.6 0 011.6 1.6v.8"></path></g></svg>
                                            </div>
                                        </div>

                                        <div>
                                            <label class="block text-sm/6 font-medium text-gray-900">
                                                {{ $t('edit_folder_modal.color_label') }}
                                            </label>
                                            <div class="mt-2 flex flex-wrap items-center gap-1.5">
                                                <button
                                                    v-for="preset in colorPresets"
                                                    :key="preset"
                                                    type="button"
                                                    @click="form.color = preset"
                                                    :title="preset"
                                                    :style="{ backgroundColor: preset }"
                                                    :class="[
                                                        'size-5 rounded-full cursor-pointer transition ring-offset-1 ring-offset-white',
                                                        form.color && form.color.toLowerCase() === preset.toLowerCase()
                                                            ? 'ring-2 ring-gray-900'
                                                            : 'ring-1 ring-gray-200 hover:ring-gray-400'
                                                    ]"
                                                />
                                                <label
                                                    class="relative size-5 rounded-full cursor-pointer overflow-hidden flex items-center justify-center ring-offset-1 ring-offset-white"
                                                    :style="isPresetColor ? { background: 'conic-gradient(from 0deg, #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6, #ef4444)' } : { backgroundColor: form.color }"
                                                    :class="!isPresetColor ? 'ring-2 ring-gray-900' : 'ring-1 ring-gray-200 hover:ring-gray-400'"
                                                    :title="$t('edit_folder_modal.color_label')"
                                                >
                                                    <input
                                                        type="color"
                                                        v-model="form.color"
                                                        class="absolute inset-0 opacity-0 cursor-pointer"
                                                    />
                                                </label>
                                            </div>
                                            <p class="mt-2 text-xs text-gray-500">{{ $t('edit_folder_modal.color_help') }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex shrink-0 justify-between px-4 py-4 bg-gray-50">
                                    <button
                                        type="button"
                                        class="cursor-pointer inline-flex items-center rounded-md gap-2 bg-red-600 px-2 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                        @click.stop="openDeleteModal"
                                    >
                                        <TrashIcon class="size-4" aria-hidden="true" />
                                        {{ $t('edit_folder_modal.delete') }}
                                    </button>
                                    <div class="flex">
                                        <button type="button" class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50 cursor-pointer" @click="close">
                                            {{ $t('edit_folder_modal.cancel') }}
                                        </button>
                                        <button type="submit" :disabled="isCreating" class="ml-4 inline-flex justify-center rounded-md bg-royal-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-royal-blue-700 cursor-pointer">
                                            {{ $t('edit_folder_modal.save') }}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <DeleteFolderConfirmationModalComponent
        :open="showDeleteModal"
        @close="showDeleteModal = false"
        @confirm-action="confirmDeleteFolder"
        :title="$t('folders.delete_confirmation_title')"
        :message="$t('folders.delete_confirmation_message')"
        :second-message="$t('folders.delete_confirmation_second_message')"
        :button-text="$t('folders.delete_confirmation_button')"
    />
</template>

<script lang="ts">
import {defineComponent} from 'vue';

import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from '@headlessui/vue'
import { XMarkIcon, TrashIcon } from '@heroicons/vue/24/outline'
import {mapActions, mapGetters} from "vuex";
import {validationMethods} from "@/_helpers/validation";
import {ResponseError} from "@/types";
import {ExclamationCircleIcon} from "@heroicons/vue/16/solid";
import {deleteFolder, updateFolder} from "@/data/requests/gallery";
import {Folder} from "@/types/GalleryType";
import DeleteFolderConfirmationModalComponent
    from "@/components/Galleries/Folders/DeleteFolderConfirmationModalComponent.vue";

export default defineComponent({
    name: 'EditFolderComponent',

    emits: ['close', 'folderDeleted'],

    components: {
        DeleteFolderConfirmationModalComponent,
        ExclamationCircleIcon,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        XMarkIcon,
        TrashIcon,
        Switch, SwitchDescription, SwitchGroup, SwitchLabel
    },

    props: {
        open: {
            type: Boolean,
            required: true,
        },

        gallerySlug: {
            type: String,
            required: true,
        },

        folder: {
            type: Object,
            required: true,
        }
    },

    data: function () {
        return {
            isCreating: false,
            showDeleteModal: false,

            colorPresets: [
                '#64748b', '#6b7280', '#000000',
                '#ef4444', '#f97316', '#f59e0b',
                '#eab308', '#84cc16', '#22c55e',
                '#10b981', '#14b8a6', '#06b6d4',
                '#0ea5e9', '#3b82f6', '#6366f1',
                '#8b5cf6', '#a855f7', '#d946ef',
                '#ec4899', '#f43f5e', '#78350f',
                '#065f46', '#1e3a8a', '#581c87',
            ],

            form: {} as Folder,

            errors: {} as { [key: string]: string[] },
        }
    },
    watch: {
        'form.title': function() {
            delete this.errors['title'];
        },
        'form.is_hidden': function() {
            delete this.errors['is_hidden'];
        },
        'form.color': function() {
            delete this.errors['is_hidden'];
        },
        'form.url': function() {
            delete this.errors['is_hidden'];
        },
    },

    computed: {
        ...mapGetters({
            gallery: 'galleries/gallery',
            folders: 'galleries/folders',
        }),

        folderTitle(): string {
            return this.form.title || this.folder.title || 'Без назви';
        },

        isPresetColor(): boolean {
            if (!this.form.color) return true;
            const current = this.form.color.toLowerCase();
            return this.colorPresets.some(c => c.toLowerCase() === current);
        },
    },
    mounted() {
        this.form = {...this.folder} as Folder;
    },
    methods: {
        close() {
            this.$emit('close');
        },

        handleDialogClose() {
            // Ігноруємо закриття Dialog, якщо відкрите модальне вікно підтвердження видалення
            if (!this.showDeleteModal) {
                this.close();
            }
        },

        ...mapActions({
            loadGalleries: "galleries/loadGalleries",
            loadFolders: "galleries/loadFolders",

            errorAlert: "alerts/error",
            successAlert: "alerts/success"
        }),

        ...validationMethods(),

        fetchFolders() {
            this.loadFolders({ gallery: this.gallerySlug })
                .catch(({ data }) => {
                    this.errorAlert({ message: this.$t('edit_folder_modal.load_error', { message: data.message }) });
                });
        },

        submit() {
            updateFolder(this.gallerySlug, this.folder.slug, this.form,
                () => {
                    this.fetchFolders();
                    this.close();
                },
                (type, data) => {
                    if (type === ResponseError.Validation) {
                        this.errors = data.data;
                        this.errorAlert({ message: this.$t('edit_folder_modal.validation_error') });
                    } else {
                        this.errorAlert({ message: data.message });
                    }
                }
            );
        },

        deleteFolder() {
            if (confirm(this.$t('edit_folder_modal.delete_confirmation') as string)) {
                deleteFolder(this.gallerySlug, this.folder.slug,
                    () => {
                        this.fetchFolders();
                        this.close();
                        this.loadFolders({ gallery: this.gallerySlug });
                        // Емітуємо подію про видалення папки
                        this.$emit('folderDeleted', this.folder.slug);
                        // Батьківський компонент сам обробить перенаправлення
                    },
                    (type, data) => {
                        if (type === ResponseError.Validation) {
                            this.errors = data.data;
                        }
                        this.errorAlert({ message: this.$t('edit_folder_modal.delete_error') });
                    }
                );
            }
        },

        openDeleteModal() {
            this.showDeleteModal = true;
        },

        confirmDeleteFolder() {
            deleteFolder(this.gallerySlug, this.folder.slug, () => {
                this.successAlert({ message: this.$t('folders.folder_deleted_success') });
                
                this.showDeleteModal = false;
                this.fetchFolders();
                this.close();

                this.$emit('folderDeleted', this.folder.slug);
            }, (type, data) => {
                this.errorAlert({
                    message: data?.message || this.$t('folders.folder_delete_error')
                });
            });
        },

        copyUrl() {
            navigator.clipboard.writeText(this.form.public_url);
            this.successAlert({ message: this.$t('edit_folder_modal.copy_success') });
        }
    }
});
</script>
